import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Eye, FileText, Copy, RefreshCw } from "lucide-react";
import PdfViewerModal from "@/components/pdf/PdfViewerModal";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card, PageHeader, Button, Field, Input, Modal, Textarea, Select } from "@/components/ui-kit";
import { AutocompleteInput } from "@/components/AutocompleteInput";
import { usePagination } from "@/hooks/usePagination";
import { formatIDR, formatDate, parseIDR, parseQty, todayISO, formatQty } from "@/lib/format";
import { DatePicker } from "@/components/DatePicker";

const blankItem = { material_name: "", unit: "", quantity: 1, unit_price: 0, location: "", notes: "" };
const blank = {
  quote_number: "", date: todayISO(), valid_until: "",
  client_name: "", client_address: "", client_npwp: "", kop_city: "", recipient_city: "",
  items: [{ ...blankItem }],
  discount: 0, use_vat: false, show_qty: true, show_location: true, show_unit: true, notes: "",
  signatory_name: "", signatory_title: "", delivery_terms: "Franco", payment_terms: "",
  // Iter v34{ad} — Bilingual: PDF akan pakai label sesuai `language`.
  language: "id",
};

export default function Quotations() {
  const { hasPerm } = useAuth();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState(blank);
  // Iter 65 (#3) — popout PDF viewer.
  const [viewer, setViewer] = useState(null);

  // master data sources
  const [clients, setClients] = useState([]);
  const [stocks, setStocks] = useState([]);
  const [units, setUnits] = useState([]);
  const [signatories, setSignatories] = useState([]);
  const [noteTemplates, setNoteTemplates] = useState([]);

  const load = () => api.get("/quotations").then((r) => setRows(r.data));
  const loadMasters = () => Promise.all([
    api.get("/clients"), api.get("/stocks"), api.get("/masters/units"),
    api.get("/signatories"), api.get("/quote-note-templates"),
  ]).then(([c, s, u, sg, nt]) => {
    setClients(c.data || []);
    setStocks(s.data || []);
    setUnits(u.data || []);
    setSignatories(sg.data || []);
    setNoteTemplates(nt.data || []);
  });

  useEffect(() => { load(); loadMasters(); }, []);

  const setItem = (i, key, val) => {
    const items = [...form.items];
    items[i] = { ...items[i], [key]: val };
    setForm({ ...form, items });
  };

  // material dropdown: pick a stock → auto-fill unit + price + location
  const pickMaterial = (i, stockId) => {
    if (!stockId) { setItem(i, "material_name", ""); return; }
    const s = stocks.find((x) => x.id === stockId);
    if (!s) return;
    const items = [...form.items];
    items[i] = {
      ...items[i],
      material_name: s.material_name,
      unit: s.unit || items[i].unit,
      location: s.category || items[i].location || "",  // category as default location source
      // price fill: use a sensible default if exists on stock; user can override.
      unit_price: items[i].unit_price || s.last_price || s.price_with_vat || items[i].unit_price,
    };
    setForm({ ...form, items });
  };

  const pickClient = (cid) => {
    if (!cid) { setForm({ ...form, client_name: "", client_address: "", client_npwp: "" }); return; }
    const c = clients.find((x) => x.id === cid);
    if (!c) return;
    setForm({ ...form, client_name: c.name, client_address: c.address || "", client_npwp: c.npwp || "" });
  };

  const pickSignatory = (sid) => {
    if (!sid) { setForm({ ...form, signatory_name: "", signatory_title: "" }); return; }
    const s = signatories.find((x) => x.id === sid);
    if (!s) return;
    setForm({ ...form, signatory_name: s.name, signatory_title: s.position || s.title || "Direktur" });
  };

  const pickNoteTemplate = (tid) => {
    if (!tid) return;
    const t = noteTemplates.find((x) => x.id === tid);
    if (!t) return;
    // append body (or replace if currently empty)
    setForm((f) => ({ ...f, notes: f.notes ? `${f.notes}\n${t.body}` : t.body }));
  };

  const suggestNextNumber = async () => {
    try {
      const r = await api.get("/quotations/next-number");
      if (r.data?.suggested) setForm((f) => ({ ...f, quote_number: r.data.suggested }));
      else toast.info("Belum ada penawaran sebelumnya — silakan isi manual");
    } catch { /* ignore */ }
  };

  const addItem = () => setForm({ ...form, items: [...form.items, { ...blankItem }] });
  const rmItem = (i) => setForm({ ...form, items: form.items.filter((_, j) => j !== i) });

  const subtotal = form.items.reduce((s, it) => s + (parseQty(it.quantity) * parseIDR(it.unit_price)), 0);
  const afterDisc = Math.max(0, subtotal - (parseIDR(form.discount) || 0));
  const vat = form.use_vat ? afterDisc * 0.11 : 0;
  const grandTotal = afterDisc + vat;

  const submit = async () => {
    if (!form.quote_number || !form.client_name) { toast.error("Nomor & klien wajib diisi"); return; }
    if (form.items.length === 0 || !form.items[0].material_name) { toast.error("Tambahkan minimal 1 item"); return; }
    const payload = {
      ...form,
      discount: parseIDR(form.discount) || 0,
      items: form.items.map((it) => ({
        ...it,
        quantity: parseQty(it.quantity),
        unit_price: parseIDR(it.unit_price),
      })),
    };
    try {
      if (edit) await api.patch(`/quotations/${edit.id}`, payload);
      else await api.post("/quotations", payload);
      toast.success(edit ? "Penawaran diperbarui" : "Penawaran dibuat");
      setOpen(false); setEdit(null); setForm(blank); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan"); }
  };

  const del = async (id) => {
    if (!window.confirm("Hapus penawaran?")) return;
    await api.delete(`/quotations/${id}`); toast.success("Dihapus"); load();
  };

  // Iter v34{ad} — AI-assisted translation via Claude Sonnet (formal business).
  const [translating, setTranslating] = useState(false);
  const translateWithAI = async () => {
    const tgt = form.language === "en" ? "en" : "id";
    const tgtLabel = tgt === "en" ? "Bahasa Inggris" : "Bahasa Indonesia";
    if (!window.confirm(`Terjemahkan item, terms & catatan ke ${tgtLabel} menggunakan AI (formal business tone)?`)) return;
    setTranslating(true);
    try {
      const res = await api.post("/quotations/translate", {
        target_language: tgt,
        items: form.items.map((it) => ({
          material_name: it.material_name || "",
          location: it.location || "",
          notes: it.notes || "",
        })),
        delivery_terms: form.delivery_terms || "",
        payment_terms: form.payment_terms || "",
        notes: form.notes || "",
        signatory_title: form.signatory_title || "",
      });
      const d = res.data || {};
      const translatedItems = (d.items || []).map((ti, i) => ({
        ...form.items[i],
        material_name: ti.material_name ?? form.items[i]?.material_name ?? "",
        location: ti.location ?? form.items[i]?.location ?? "",
        notes: ti.notes ?? form.items[i]?.notes ?? "",
      }));
      setForm({
        ...form,
        items: translatedItems.length ? translatedItems : form.items,
        delivery_terms: d.delivery_terms ?? form.delivery_terms,
        payment_terms: d.payment_terms ?? form.payment_terms,
        notes: d.notes ?? form.notes,
        signatory_title: d.signatory_title || form.signatory_title,
      });
      toast.success(`Berhasil diterjemahkan ke ${tgtLabel}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menerjemahkan");
    } finally {
      setTranslating(false);
    }
  };

  const _formatItemForDisplay = (it) => ({
    ...it,
    quantity: it.quantity !== undefined && it.quantity !== null ? formatQty(it.quantity) : "",
    unit_price: it.unit_price !== undefined && it.unit_price !== null ? formatQty(it.unit_price) : "",
  });

  const openEdit = (q) => {
    setEdit(q);
    setForm({
      ...blank, ...q,
      discount: q.discount ? formatQty(q.discount) : 0,
      items: (q.items || []).map((i) => _formatItemForDisplay({ ...blankItem, ...i })),
    });
    setOpen(true);
  };
  const openNew = () => { setEdit(null); setForm({ ...blank, items: [{ ...blankItem }] }); setOpen(true); };
  const openDuplicate = (q) => {
    setEdit(null);
    setForm({
      ...blank, ...q,
      quote_number: "", date: todayISO(),
      discount: q.discount ? formatQty(q.discount) : 0,
      items: (q.items || []).map((i) => _formatItemForDisplay({ ...blankItem, ...i })),
    });
    setOpen(true);
    toast.success("Duplikat siap — isi No Penawaran baru lalu simpan");
  };

  const buildPdfUrl = (id) => `${process.env.REACT_APP_BACKEND_URL}/api/quotations/${id}/pdf`;

  const downloadPdf = async (id, num, client) => {
    try {
      const r = await api.get(`/quotations/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      const safe = (s) => String(s || "").replace(/[^A-Za-z0-9._-]+/g, "_").replace(/_+/g, "_");
      a.download = `penawaran_${safe(num || id)}${client ? `_${safe(client)}` : ""}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Gagal download PDF"); }
  };

  const printPdf = async (id) => {
    try {
      const r = await api.get(`/quotations/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const w = window.open(url, "_blank");
      if (w) w.addEventListener("load", () => { try { w.focus(); w.print(); } catch { /* ignore */ } });
    } catch (e) { toast.error("Gagal cetak PDF"); }
  };

  // dropdown options
  const stockMatches = useMemo(() => stocks.map((s) => ({
    id: s.id, label: `${s.material_name}${s.unit ? ` (${s.unit})` : ""}${s.category ? ` · ${s.category}` : ""}`,
  })), [stocks]);

  const { pageItems, Pagination } = usePagination(rows, { perPage: 50 });

  return (
    <div className="min-h-full bg-[#f6f5f0]">
    <div className="max-w-[1600px] mx-auto p-4 md:p-6 lg:p-8 space-y-6">
      <PageHeader
        overline="Sales · Penawaran"
        title="Penawaran Harga"
        description="Buat, edit, dan download PDF penawaran harga untuk klien."
        action={hasPerm("quotation.create") && <Button onClick={openNew} data-testid="add-quote-btn"><Plus className="h-4 w-4" /> Penawaran Baru</Button>}
      />

      {/* Bento KPI hero */}
      {(() => {
        const total = rows.length;
        const nilaiTotal = rows.reduce((s, r) => s + (Number(r.total) || 0), 0);
        const kliens = new Set(rows.map((r) => r.client_name)).size;
        // Recent 30 days
        const now = Date.now();
        const recent = rows.filter((r) => {
          const d = r.date ? new Date(r.date).getTime() : 0;
          return d && (now - d) < 30 * 24 * 3600 * 1000;
        }).length;
        return (
          <div className="grid grid-cols-12 gap-3">
            <div className="col-span-12 md:col-span-5 rounded-2xl bg-gradient-to-br from-stone-900 via-stone-800 to-blue-950 text-white p-6 relative overflow-hidden">
              <div className="absolute -right-16 -top-16 w-56 h-56 rounded-full bg-blue-500/20 blur-3xl"></div>
              <div className="relative">
                <div className="text-[11px] uppercase tracking-widest text-stone-400">Total Nilai Penawaran</div>
                <div className="font-display text-4xl mt-2 font-mono-num">Rp {nilaiTotal.toLocaleString("id-ID")}</div>
                <div className="mt-3 text-xs text-stone-400">{total} penawaran · {kliens} klien unik</div>
              </div>
            </div>
            <div className="col-span-6 md:col-span-3 rounded-2xl bg-white border border-stone-200/70 p-4">
              <div className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold">Total Penawaran</div>
              <div className="font-display text-3xl mt-1 text-stone-900">{total}</div>
            </div>
            <div className="col-span-6 md:col-span-2 rounded-2xl bg-emerald-50/60 border border-emerald-100 p-4">
              <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold">30 Hari</div>
              <div className="font-display text-3xl mt-1 text-emerald-800">{recent}</div>
            </div>
            <div className="col-span-12 md:col-span-2 rounded-2xl bg-blue-50/60 border border-blue-100 p-4">
              <div className="text-[10px] uppercase tracking-wider text-blue-700 font-semibold">Klien Unik</div>
              <div className="font-display text-3xl mt-1 text-blue-800">{kliens}</div>
            </div>
          </div>
        );
      })()}

      <Card className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-stone-50 border-b border-stone-200 text-left text-[10px] font-semibold uppercase tracking-wider text-stone-600">
              <th className="py-3 px-3">No Penawaran</th>
              <th className="py-3 px-3">Tanggal</th>
              <th className="py-3 px-3">Klien</th>
              <th className="py-3 px-3">Berlaku s/d</th>
              <th className="py-3 px-3 text-right">Total</th>
              <th className="py-3 px-3 text-right">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={6} className="py-12 text-center text-stone-400">Belum ada penawaran.</td></tr>}
            {pageItems.map((q, idx) => (
              <tr key={q.id} className="border-b border-stone-100 hover:bg-stone-50/50" data-testid={`quote-row-${idx}`}>
                <td className="py-2.5 px-3 font-mono font-medium">{q.quote_number}</td>
                <td className="py-2.5 px-3 font-mono text-stone-600">{formatDate(q.date)}</td>
                <td className="py-2.5 px-3 font-medium">{q.client_name}</td>
                <td className="py-2.5 px-3 font-mono text-stone-500">{q.valid_until || "—"}</td>
                <td className="py-2.5 px-3 text-right font-mono-num font-semibold">{formatIDR(q.grand_total || 0)}</td>
                <td className="py-2.5 px-3 text-right">
                  <button onClick={() => setViewer(q)} className="text-stone-500 hover:text-terra-700 px-2" title="Lihat PDF" data-testid={`quote-view-${idx}`}><Eye className="h-3.5 w-3.5" /></button>
                  {hasPerm("quotation.create") && <button onClick={() => openDuplicate(q)} className="text-stone-500 hover:text-terra-700 px-2" title="Duplikat" data-testid={`quote-duplicate-${idx}`}><Copy className="h-3.5 w-3.5" /></button>}
                  {hasPerm("quotation.edit") && <button onClick={() => openEdit(q)} className="text-stone-500 hover:text-terra-700 px-2"><Pencil className="h-3.5 w-3.5" /></button>}
                  {hasPerm("quotation.delete") && <button onClick={() => del(q.id)} className="text-stone-500 hover:text-red-700 px-2"><Trash2 className="h-3.5 w-3.5" /></button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
      {Pagination}

      <Modal open={open} onClose={() => setOpen(false)} title={edit ? "Edit Penawaran" : "Buat Penawaran Baru"} size="xl">
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Field label="No Penawaran" required hint={!edit ? "Klik tombol di kanan untuk usulan nomor berikutnya" : ""}>
              <div className="flex gap-1">
                <Input className="flex-1" value={form.quote_number} onChange={(e) => setForm({ ...form, quote_number: e.target.value })} placeholder="QH/001/IX/26" data-testid="quote-number" />
                {!edit && (
                  <Button variant="secondary" type="button" onClick={suggestNextNumber} className="!px-2" title="Usulan nomor berikutnya" data-testid="quote-suggest-num">
                    <RefreshCw className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            </Field>
            <Field label="Tanggal" required>
              <DatePicker value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="quote-date" />
            </Field>
            <Field label="Berlaku s/d">
              <DatePicker value={form.valid_until} onChange={(e) => setForm({ ...form, valid_until: e.target.value })} data-testid="quote-valid-until" />
            </Field>

            <Field label="Klien (pilih dari daftar)" required>
              <Select onChange={(e) => pickClient(e.target.value)} value="" data-testid="quote-client-select">
                <option value="">— Pilih Klien —</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </Select>
            </Field>
            <Field label="Nama Klien (dapat diubah)" required>
              <Input value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} placeholder="PT ..." data-testid="quote-client-name" />
            </Field>
            <Field label="NPWP Klien">
              <Input value={form.client_npwp} onChange={(e) => setForm({ ...form, client_npwp: e.target.value })} placeholder="00.000.000.0-000.000" />
            </Field>
            <Field label="Lokasi Perusahaan (di samping kiri tanggal)" hint="Default: Palembang. Pilih dari riwayat atau ketik manual.">
              <AutocompleteInput
                field="quotation.kop_city"
                value={form.kop_city}
                onChange={(v) => setForm({ ...form, kop_city: v })}
                placeholder="Palembang"
                dataTestid="quote-kop-city"
              />
            </Field>
            <Field label="Lokasi Kantor Klien (di bawah 'di')" required hint="Tampil di kop surat di bawah nama klien. Pilih dari riwayat atau ketik manual.">
              <AutocompleteInput
                field="quotation.recipient_city"
                value={form.recipient_city}
                onChange={(v) => setForm({ ...form, recipient_city: v })}
                placeholder="Sungai Baung"
                dataTestid="quote-recipient-city"
              />
            </Field>
            <Field label="Alamat Klien (untuk arsip, tidak dicetak di kop)" className="md:col-span-3">
              <Textarea rows={2} value={form.client_address} onChange={(e) => setForm({ ...form, client_address: e.target.value })} data-testid="quote-client-address" />
            </Field>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-semibold uppercase tracking-wider text-stone-600">Item Penawaran</label>
              <Button variant="secondary" className="!py-1 !px-2 text-xs" onClick={addItem}><Plus className="h-3 w-3" /> Tambah Item</Button>
            </div>
            <div className="border border-stone-200">
              {(() => {
                const cols = [
                  "3fr", // material pick
                  "3fr", // material name
                  form.show_location ? "2fr" : null,
                  form.show_unit ? "1.2fr" : null,
                  "1.5fr", // qty
                  "2fr",   // harga satuan
                  "0.6fr", // delete
                ].filter(Boolean).join(" ");
                return (
                  <>
                    <div className="gap-1.5 p-2 bg-stone-50 text-[10px] uppercase tracking-wider font-semibold text-stone-600 border-b border-stone-200" style={{ display: "grid", gridTemplateColumns: cols }}>
                      <div>Material (Pilih / Manual)</div>
                      <div>Nama Material</div>
                      {form.show_location && <div>Lokasi</div>}
                      {form.show_unit && <div>Satuan</div>}
                      <div className="text-right">Qty</div>
                      <div className="text-right">Harga Satuan</div>
                      <div></div>
                    </div>
                    {form.items.map((it, i) => (
                      <div key={i} className="gap-1.5 p-2 border-b border-stone-100 items-center" style={{ display: "grid", gridTemplateColumns: cols }}>
                        <Select value="" onChange={(e) => pickMaterial(i, e.target.value)} data-testid={`quote-item-pick-${i}`}>
                          <option value="">— Pilih dari Stok —</option>
                          {stockMatches.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                        </Select>
                        <Input value={it.material_name} onChange={(e) => setItem(i, "material_name", e.target.value)} placeholder="Nama material" data-testid={`quote-item-name-${i}`} />
                        {form.show_location && (
                          <Input value={it.location} onChange={(e) => setItem(i, "location", e.target.value)} placeholder="Lokasi (opsional)" data-testid={`quote-item-location-${i}`} />
                        )}
                        {form.show_unit && (
                          <Select value={it.unit} onChange={(e) => setItem(i, "unit", e.target.value)} data-testid={`quote-item-unit-${i}`}>
                            <option value="">—</option>
                            {units.map((u) => <option key={u.id} value={u.name}>{u.name}</option>)}
                            {it.unit && !units.find((u) => u.name === it.unit) && <option value={it.unit}>{it.unit}</option>}
                          </Select>
                        )}
                        <Input className="font-mono-num text-right" value={it.quantity} onChange={(e) => setItem(i, "quantity", e.target.value)} placeholder="0,00" data-testid={`quote-item-qty-${i}`} />
                        <Input className="font-mono-num text-right" value={it.unit_price} onChange={(e) => setItem(i, "unit_price", e.target.value)} placeholder="0" data-testid={`quote-item-price-${i}`} />
                        <button onClick={() => rmItem(i)} className="text-stone-400 hover:text-red-700 justify-self-center"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    ))}
                  </>
                );
              })()}
            </div>
            <p className="text-[10px] text-stone-500 mt-1">Tip: pilih dari Stok untuk auto-isi nama, satuan & lokasi. Semua kolom tetap bisa diubah manual.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-3 border-t border-stone-200">
            <div className="space-y-3">
              <Field label="Syarat Pengiriman">
                <Select value={form.delivery_terms} onChange={(e) => setForm({ ...form, delivery_terms: e.target.value })}>
                  <option value="Franco">Franco</option>
                  <option value="Loco">Loco</option>
                </Select>
              </Field>
              <Field label="Syarat Pembayaran">
                <Input value={form.payment_terms} onChange={(e) => setForm({ ...form, payment_terms: e.target.value })} placeholder="Net 30 / Cash / Termin 50%" />
              </Field>
              <Field label="Catatan" hint="Pilih template (opsional) — hanya isi catatan yang akan dicetak, judul tidak ditampilkan di PDF">
                <div className="space-y-1.5">
                  <Select value="" onChange={(e) => pickNoteTemplate(e.target.value)} data-testid="quote-note-template">
                    <option value="">— Pilih Template Catatan —</option>
                    {noteTemplates.map((t) => <option key={t.id} value={t.id}>{t.title}</option>)}
                  </Select>
                  <Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Catatan tambahan…" />
                </div>
              </Field>
              <Field label="Penanda Tangan">
                <div className="space-y-1.5">
                  <Select value="" onChange={(e) => pickSignatory(e.target.value)} data-testid="quote-signatory-select">
                    <option value="">— Pilih dari Daftar —</option>
                    {signatories.map((s) => <option key={s.id} value={s.id}>{s.name}{s.position ? ` · ${s.position}` : ""}</option>)}
                  </Select>
                  <Input value={form.signatory_name} onChange={(e) => setForm({ ...form, signatory_name: e.target.value })} placeholder="Nama penanda tangan" />
                </div>
              </Field>
              <Field label="Jabatan">
                <Input value={form.signatory_title} onChange={(e) => setForm({ ...form, signatory_title: e.target.value })} placeholder="Direktur" />
              </Field>
              {/* Iter v34{ad} — Language toggle + AI translate button */}
              <Field label="Bahasa PDF">
                <div className="flex gap-2">
                  <Select value={form.language || "id"} onChange={(e) => setForm({ ...form, language: e.target.value })} data-testid="quote-language">
                    <option value="id">Bahasa Indonesia</option>
                    <option value="en">English</option>
                  </Select>
                  <Button variant="ghost" onClick={translateWithAI} disabled={translating} type="button" data-testid="quote-translate-ai-btn" title="Terjemahkan isi item, terms & catatan dengan AI (formal business)">
                    {translating ? "Menerjemahkan…" : "AI Translate"}
                  </Button>
                </div>
              </Field>
            </div>
            <div className="space-y-2 bg-stone-50 p-3 border border-stone-200">
              <div className="flex justify-between text-sm"><span>Sub Total</span><span className="font-mono-num">{formatIDR(subtotal)}</span></div>
              <Field label="Diskon (Rp)"><Input className="font-mono-num text-right" value={form.discount} onChange={(e) => setForm({ ...form, discount: e.target.value })} placeholder="0" /></Field>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.use_vat} onChange={(e) => setForm({ ...form, use_vat: e.target.checked })} data-testid="quote-use-vat" /> Sertakan PPN 11%</label>
              <label className="flex items-center gap-2 text-sm" title="Jika nonaktif, kolom Qty + Jumlah serta baris Sub Total, PPN, TOTAL, dan Terbilang tidak akan tampil di PDF Penawaran (Satuan tetap selalu tampil)"><input type="checkbox" checked={form.show_qty} onChange={(e) => setForm({ ...form, show_qty: e.target.checked })} data-testid="quote-show-qty" /> Tampilkan Qty &amp; Jumlah/Total di PDF</label>
              <label className="flex items-center gap-2 text-sm" title="Jika nonaktif, kolom Lokasi tidak akan tampil di form input maupun PDF Penawaran"><input type="checkbox" checked={form.show_location} onChange={(e) => setForm({ ...form, show_location: e.target.checked })} data-testid="quote-show-location" /> Tampilkan Lokasi di PDF</label>
              <label className="flex items-center gap-2 text-sm" title="Jika nonaktif, kolom Satuan tidak akan tampil di form input maupun PDF Penawaran"><input type="checkbox" checked={form.show_unit} onChange={(e) => setForm({ ...form, show_unit: e.target.checked })} data-testid="quote-show-unit" /> Tampilkan Satuan di PDF</label>
              {form.use_vat && <div className="flex justify-between text-sm"><span>PPN 11%</span><span className="font-mono-num">{formatIDR(vat)}</span></div>}
              <div className="flex justify-between text-base font-bold border-t border-stone-300 pt-2"><span>TOTAL</span><span className="font-mono-num">{formatIDR(grandTotal)}</span></div>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-3 border-t border-stone-200">
            <Button variant="ghost" onClick={() => setOpen(false)}>Batal</Button>
            <Button onClick={submit} data-testid="quote-save-btn">{edit ? "Simpan" : "Buat Penawaran"}</Button>
          </div>
        </div>
      </Modal>
      {/* Iter 65 (#3) — Popout PDF viewer */}
      <PdfViewerModal
        open={!!viewer}
        onClose={() => setViewer(null)}
        pdfUrl={viewer ? `${process.env.REACT_APP_BACKEND_URL}/api/quotations/${viewer.id}/pdf` : ""}
        fileName={`Penawaran-${viewer?.quote_number || viewer?.id || "doc"}.pdf`}
        title={`Penawaran ${viewer?.quote_number || ""}`}
      />
    </div>
    </div>
  );
}
